import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { resolveGuideImageUrl } from '../../utils/guideImageUrl';
import { collectAllGuides, resolveGuidesHome } from '../../utils/vaGuidesHome';
import { GuideStepMedia } from '../../components/GuideStepMedia';
import VAGuidesHome from './VAGuidesHome';
import {
  BookOpenIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

function CategoryHeader({ category }) {
  const src = resolveGuideImageUrl(category.imageUrl, category.displayUrl);
  return (
    <div className="flex items-center gap-3 mb-4">
      {src ? (
        <img
          src={src}
          alt=""
          className="w-12 h-12 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 shadow-sm"
        />
      ) : (
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
          {(category.title || 'G').charAt(0).toUpperCase()}
        </div>
      )}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{category.title}</h2>
        <p className="text-xs text-slate-500">
          {(category.guides || []).length} guide{(category.guides || []).length === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );
}

function GuideCard({ guide, expandedId, setExpandedId, guideRef }) {
  const open = expandedId === guide._id;
  const steps = guide.steps || [];

  return (
    <div
      ref={guideRef}
      id={`guide-${guide._id}`}
      className="scroll-mt-24 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpandedId(open ? null : guide._id)}
        className="w-full px-6 py-4 flex items-start justify-between gap-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{guide.title}</h3>
          {guide.description && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{guide.description}</p>
          )}
          <p className="mt-2 text-xs text-slate-400">{steps.length} step{steps.length !== 1 ? 's' : ''}</p>
        </div>
        {open ? (
          <ChevronUpIcon className="w-5 h-5 flex-shrink-0 text-slate-400 mt-1" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 flex-shrink-0 text-slate-400 mt-1" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 dark:border-slate-800 pt-4 space-y-8">
          {steps.length === 0 && (
            <p className="text-sm text-slate-500">No steps in this guide yet.</p>
          )}
          {steps.map((step, idx) => (
            <div key={step._id || idx} className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 dark:text-white">
                  {step.title || `Step ${idx + 1}`}
                </h4>
                {step.body && (
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {step.body}
                  </p>
                )}
                <GuideStepMedia step={step} alt={step.title || `Step ${idx + 1}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VAGuides = () => {
  const [categories, setCategories] = useState([]);
  const [uncategorizedGuides, setUncategorizedGuides] = useState([]);
  const [guidesHome, setGuidesHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const guideRefs = useRef(new Map());

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/va/guides');
        if (res.data.success) {
          setCategories(res.data.categories || []);
          setUncategorizedGuides(res.data.uncategorizedGuides || []);
          setGuidesHome(resolveGuidesHome(res.data.guidesHome));
        }
      } catch (err) {
        toast.error('Failed to load guides');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allGuides = useMemo(
    () => collectAllGuides(categories, uncategorizedGuides),
    [categories, uncategorizedGuides]
  );

  const totalGuides = allGuides.length;
  const hasCategories = categories.length > 0;

  if (loading) {
    return <div className="text-slate-500 py-12 text-center">Loading guides...</div>;
  }

  return (
    <div className="space-y-10">
      <VAGuidesHome guides={allGuides} guidesHome={guidesHome} />

      {totalGuides > 0 && (
        <section className="space-y-6 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-indigo-600" />
              All guides (with screenshots & videos)
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Open a guide to see each step. Click screenshots to enlarge; use the video player for video steps.
            </p>
          </div>

          {categories.map((category) => (
            <div key={category._id} className="space-y-4">
              <CategoryHeader category={category} />
              <div className="space-y-4 pl-0 sm:pl-2">
                {(category.guides || []).map((guide) => (
                  <GuideCard
                    key={guide._id}
                    guide={guide}
                    expandedId={expandedId}
                    setExpandedId={setExpandedId}
                    guideRef={(node) => {
                      if (node) guideRefs.current.set(String(guide._id), node);
                      else guideRefs.current.delete(String(guide._id));
                    }}
                  />
                ))}
              </div>
            </div>
          ))}

          {uncategorizedGuides.length > 0 && (
            <div className="space-y-4">
              {hasCategories && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <FolderIcon className="w-6 h-6 text-slate-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Other guides</h2>
                    <p className="text-xs text-slate-500">
                      {uncategorizedGuides.length} guide{uncategorizedGuides.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {uncategorizedGuides.map((guide) => (
                  <GuideCard
                    key={guide._id}
                    guide={guide}
                    expandedId={expandedId}
                    setExpandedId={setExpandedId}
                    guideRef={(node) => {
                      if (node) guideRefs.current.set(String(guide._id), node);
                      else guideRefs.current.delete(String(guide._id));
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {totalGuides === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <BookOpenIcon className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">
            Screenshot guides are not published yet. Follow the steps above and ask your manager if you need help.
          </p>
        </div>
      )}
    </div>
  );
};

export default VAGuides;
